import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { AppButton, AppNumberField } from '../../../../Components/FormKit';
import { url } from '../../../../env';

const IVA = 1.16;

const fmtMoney = (n) => new Intl.NumberFormat('es-MX', {
    style: 'currency', currency: 'MXN', minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(Number(n) || 0);

const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

// --- estilos card del sistema (radius 14, sombra suave, borde --pei-line) ---
const cardBox = {
    background: '#fff', border: '1px solid var(--pei-line, #e2e8f0)', borderRadius: 14,
    boxShadow: '0 1px 6px rgba(0,0,0,.06)', overflow: 'hidden',
};
const cardHeaderStyle = {
    padding: '12px 18px', background: 'var(--pei-bg-soft, #f8fafc)', borderBottom: '1px solid var(--pei-line, #e2e8f0)',
    display: 'flex', alignItems: 'center', gap: 10,
};
const cardBody = { padding: 18 };
const fieldBox = {
    border: '1px solid var(--pei-line, #e2e8f0)', borderRadius: 12, background: '#fff',
    padding: 14, height: '100%', display: 'flex', flexDirection: 'column', gap: 10,
};

export const TasasFinancieras = ({ toast }) => {
    const [loading, setLoading] = useState(true);
    const [savingKey, setSavingKey] = useState(null); // 'tasa-Nuevo' | 'tasa-Seminuevo' | ... | 'gps'

    // Valores en edición
    const [tasaNuevo, setTasaNuevo] = useState(0);
    const [tasaSeminuevo, setTasaSeminuevo] = useState(0);
    const [commNuevo, setCommNuevo] = useState(0);
    const [commSeminuevo, setCommSeminuevo] = useState(0);
    const [gps, setGps] = useState(0);

    const fetchValues = useCallback(async () => {
        try {
            const res = await axios.get(`${url}/valores-calculadora/financial-values`, { headers: authHeaders() });
            const d = res.data?.data || {};
            setTasaNuevo(Number(d.tasa) || 0);
            setTasaSeminuevo(Number(d.tasa_seminuevo) || 0);
            setCommNuevo(Number(d.commission) || 0);
            setCommSeminuevo(Number(d.commission_seminuevo) || 0);
            setGps(Number(d.gps) || 0);
        } catch (error) {
            console.error('Error al obtener valores financieros:', error);
            toast?.current?.show({
                severity: 'error', summary: 'Error',
                detail: 'No se pudieron cargar las tasas globales.', life: 5000,
            });
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => { fetchValues(); }, [fetchValues]);

    // type: 'tasa' | 'commission' | 'gps'. typeAuto: 'Nuevo' | 'Seminuevo' (no aplica a gps)
    const save = async (type, value, typeAuto) => {
        const key = type === 'gps' ? 'gps' : `${type}-${typeAuto}`;
        setSavingKey(key);
        try {
            const payload = { [type]: value };
            if (typeAuto) payload.typeAuto = typeAuto;
            const res = await axios.post(
                `${url}/valores-calculadora/update-financial-values`,
                payload,
                { headers: authHeaders() },
            );
            if (res.data?.status === 'success') {
                toast?.current?.show({
                    severity: 'success', summary: 'Guardado',
                    detail: 'Valor global actualizado. Aplica a todas las cotizaciones futuras.', life: 4000,
                });
                await fetchValues(); // refresca por el ajuste automático del par Nuevo/Seminuevo
            }
        } catch (error) {
            console.error('Error al guardar valor:', error);
            toast?.current?.show({
                severity: 'error', summary: 'Error',
                detail: 'No se pudo guardar el valor. Intenta de nuevo.', life: 5000,
            });
        } finally {
            setSavingKey(null);
        }
    };

    // Campo de porcentaje (Nuevo/Seminuevo): AppNumberField + preview "con IVA" coloreada + guardar.
    const PercentField = ({ label, value, onChange, onSave, saving, previewColor }) => (
        <div style={fieldBox}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <i className="pi pi-car" style={{ color: 'var(--pei-text-helper, #64748b)', fontSize: 14 }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--pei-text, #0F172A)' }}>{label}</span>
            </div>
            <AppNumberField
                value={value}
                onValueChange={(e) => onChange(e.value)}
                mode="decimal" min={0} max={100} minFractionDigits={0} maxFractionDigits={2}
                suffix="%"
                helperText="Captura el porcentaje. Ej: 25 = 25 %."
            />
            <div style={{ fontSize: 12.5, fontWeight: 600, color: previewColor }}>
                con IVA: {(Number(value) * IVA).toFixed(2)} %
            </div>
            <div style={{ marginTop: 'auto' }}>
                <AppButton label="Guardar" icon="pi pi-check" variant="primary" size="sm" onClick={onSave} loading={saving} />
            </div>
        </div>
    );

    if (loading) {
        return (
            <section className="fk-root" style={{ padding: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--pei-text-helper, #64748b)' }}>
                    <i className="pi pi-spin pi-spinner" /> Cargando tasas globales…
                </div>
            </section>
        );
    }

    return (
        <section className="fk-root" style={{ padding: 16, fontFamily: 'var(--pei-font)' }}>
            {/* Encabezado de sección */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <i className="pi pi-percentage" style={{ color: 'var(--pei-cyan, #32B8DF)', fontSize: 20 }} />
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--pei-text, #0F172A)' }}>
                    Tasas y comisiones globales
                </h2>
            </div>
            <p style={{ fontSize: 13, color: 'var(--pei-text-helper, #64748b)', margin: '0 0 18px', maxWidth: 760 }}>
                Estos valores se aplican a <strong>todas las cotizaciones futuras</strong> del sistema (calculadoras 1 y 2).
                Al guardar un valor de Nuevo o Seminuevo, su par se ajusta automáticamente según la regla del sistema
                (Seminuevo = Nuevo + 2 % en tasa, + 1 % en comisión).
            </p>

            {/* TASA DE INTERÉS */}
            <div style={{ ...cardBox, marginBottom: 18 }}>
                <div style={cardHeaderStyle}>
                    <i className="pi pi-chart-line" style={{ color: 'var(--pei-cyan, #32B8DF)' }} />
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--pei-text, #0F172A)' }}>Tasa de interés</h3>
                </div>
                <div style={cardBody}>
                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <PercentField
                                label="Vehículo Nuevo"
                                value={tasaNuevo} onChange={setTasaNuevo}
                                onSave={() => save('tasa', tasaNuevo, 'Nuevo')}
                                saving={savingKey === 'tasa-Nuevo'} previewColor="var(--pei-cyan, #32B8DF)"
                            />
                        </div>
                        <div className="col-12 md:col-6">
                            <PercentField
                                label="Vehículo Seminuevo"
                                value={tasaSeminuevo} onChange={setTasaSeminuevo}
                                onSave={() => save('tasa', tasaSeminuevo, 'Seminuevo')}
                                saving={savingKey === 'tasa-Seminuevo'} previewColor="var(--pei-cyan, #32B8DF)"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* COMISIÓN */}
            <div style={{ ...cardBox, marginBottom: 18 }}>
                <div style={cardHeaderStyle}>
                    <i className="pi pi-wallet" style={{ color: 'var(--pei-green-true, #00BA6E)' }} />
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--pei-text, #0F172A)' }}>Comisión por apertura</h3>
                </div>
                <div style={cardBody}>
                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <PercentField
                                label="Vehículo Nuevo"
                                value={commNuevo} onChange={setCommNuevo}
                                onSave={() => save('commission', commNuevo, 'Nuevo')}
                                saving={savingKey === 'commission-Nuevo'} previewColor="var(--pei-green-true, #00BA6E)"
                            />
                        </div>
                        <div className="col-12 md:col-6">
                            <PercentField
                                label="Vehículo Seminuevo"
                                value={commSeminuevo} onChange={setCommSeminuevo}
                                onSave={() => save('commission', commSeminuevo, 'Seminuevo')}
                                saving={savingKey === 'commission-Seminuevo'} previewColor="var(--pei-green-true, #00BA6E)"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* GPS */}
            <div style={{ ...cardBox, marginBottom: 18 }}>
                <div style={cardHeaderStyle}>
                    <i className="pi pi-map-marker" style={{ color: 'var(--pei-orange, #FF6C0E)' }} />
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--pei-text, #0F172A)' }}>Costo de GPS</h3>
                    <span style={{ fontSize: 12, color: 'var(--pei-text-helper, #64748b)', fontWeight: 400 }}>
                        (único, no depende del tipo de vehículo)
                    </span>
                </div>
                <div style={cardBody}>
                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <div style={fieldBox}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <i className="pi pi-map-marker" style={{ color: 'var(--pei-orange, #FF6C0E)', fontSize: 14 }} />
                                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--pei-text, #0F172A)' }}>Costo unitario de GPS</span>
                                </div>
                                <AppNumberField
                                    value={gps}
                                    onValueChange={(e) => setGps(e.value)}
                                    mode="currency" currency="MXN" locale="es-MX" min={0}
                                    helperText="Monto unitario en pesos."
                                />
                                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--pei-orange, #FF6C0E)' }}>
                                    con IVA: {fmtMoney(Number(gps) * IVA)}
                                </div>
                                <div style={{ marginTop: 'auto' }}>
                                    <AppButton label="Guardar" icon="pi pi-check" variant="primary" size="sm"
                                        onClick={() => save('gps', gps, null)} loading={savingKey === 'gps'} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div style={{ background: '#ECF8FE', border: '1px solid rgba(50,184,223,0.30)', borderRadius: 12, padding: '0.8rem 1rem' }}
                className="flex align-items-start gap-2">
                <i className="pi pi-info-circle" style={{ color: 'var(--pei-cyan, #32B8DF)', marginTop: 2 }} />
                <span style={{ fontSize: 13, color: '#075985' }}>
                    Para cambiar la tasa de <strong>una sola cotización</strong> sin afectar a todo el sistema,
                    usa “Cambiar tasa de esta cotización” dentro de la cotización.
                </span>
            </div>
        </section>
    );
};

export default TasasFinancieras;
